import { useEffect, useState } from "react"
import { ToastMessage, useToast } from "@stayframe/ui"
import { formatApiError } from "@/lib/api"
import {
  opsAdminApi,
  type OpsUserRow,
  type OpsUserDetail,
  type OpsUserProperty,
  type OpsUserSession,
  type OpsUserAuditEntry,
} from "@/lib/opsAdminApi"

type ActiveFilter = "" | "active" | "inactive"
type RoleFilter = "" | "admin" | "platform_admin" | "owner"

export function OpsUsersPage() {
  const { toast } = useToast()

  const [users, setUsers] = useState<OpsUserRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("")
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("")
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)

  // Drawer state
  const [selectedUserId, setSelectedUserId] = useState("")
  const [drawerUser, setDrawerUser] = useState<OpsUserDetail | null>(null)
  const [drawerProperties, setDrawerProperties] = useState<OpsUserProperty[]>([])
  const [drawerSessions, setDrawerSessions] = useState<OpsUserSession[]>([])
  const [drawerAudit, setDrawerAudit] = useState<OpsUserAuditEntry[]>([])
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [actionBusy, setActionBusy] = useState("")

  function showApiError(result: Record<string, unknown>, fallback = "Request failed.") {
    const err = formatApiError(result)
    toast(<ToastMessage message={err.message || fallback} requestId={err.requestId} />, "error")
  }

  async function loadUsers(append = false) {
    if (append) setLoadingMore(true)
    else setLoading(true)

    const res = await opsAdminApi.getOpsUsers({
      search: search.trim() || undefined,
      active: activeFilter || undefined,
      role: roleFilter || undefined,
      cursor: append ? (nextCursor || undefined) : undefined,
      limit: 50,
    })

    if (res.ok && res.users) {
      setUsers(append ? (prev) => [...prev, ...res.users!] : res.users)
      setHasMore(Boolean(res.has_more))
      setNextCursor(res.next_cursor || null)
    } else {
      if (!append) setUsers([])
      showApiError(res as unknown as Record<string, unknown>, "Failed to load users.")
    }

    if (append) setLoadingMore(false)
    else setLoading(false)
  }

  async function openUserDrawer(userId: string) {
    setSelectedUserId(userId)
    setDrawerLoading(true)
    setDrawerUser(null)
    setDrawerProperties([])
    setDrawerSessions([])
    setDrawerAudit([])

    const res = await opsAdminApi.getOpsUserDetail(userId)
    if (res.ok && res.user) {
      setDrawerUser(res.user)
      setDrawerProperties(res.properties || [])
      setDrawerSessions(res.sessions || [])
      setDrawerAudit(res.recent_audit || [])
    } else {
      showApiError(res as unknown as Record<string, unknown>, "Failed to load user detail.")
    }
    setDrawerLoading(false)
  }

  function closeDrawer() {
    setSelectedUserId("")
    setDrawerUser(null)
    setDrawerProperties([])
    setDrawerSessions([])
    setDrawerAudit([])
    setDrawerLoading(false)
  }

  async function handleToggleActive() {
    if (!drawerUser) return
    setActionBusy("toggle_active")
    const next = !drawerUser.is_active
    const res = await opsAdminApi.updateOpsUser(drawerUser.id, { is_active: next })
    if (res.ok) {
      toast(next ? "User activated." : "User deactivated.", "success")
      setDrawerUser({ ...drawerUser, is_active: next })
      setUsers((prev) => prev.map((u) => u.id === drawerUser.id ? { ...u, is_active: next } : u))
    } else {
      showApiError(res as unknown as Record<string, unknown>, "Failed to update user.")
    }
    setActionBusy("")
  }

  async function handleToggleRole(role: string) {
    if (!drawerUser) return
    setActionBusy("toggle_role")
    const res = await opsAdminApi.updateOpsUser(drawerUser.id, { role })
    if (res.ok) {
      toast(`Role updated to ${role}.`, "success")
      setDrawerUser({ ...drawerUser, role })
      setUsers((prev) => prev.map((u) => u.id === drawerUser.id ? { ...u, role } : u))
    } else {
      showApiError(res as unknown as Record<string, unknown>, "Failed to update role.")
    }
    setActionBusy("")
  }

  async function handleTogglePlatformAdmin() {
    if (!drawerUser) return
    setActionBusy("toggle_platform_admin")
    const next = !drawerUser.is_platform_admin
    const res = await opsAdminApi.updateOpsUser(drawerUser.id, { is_platform_admin: next })
    if (res.ok) {
      toast(next ? "Platform admin granted." : "Platform admin revoked.", "success")
      setDrawerUser({ ...drawerUser, is_platform_admin: next })
      setUsers((prev) => prev.map((u) => u.id === drawerUser.id ? { ...u, is_platform_admin: next } : u))
    } else {
      showApiError(res as unknown as Record<string, unknown>, "Failed to update platform admin flag.")
    }
    setActionBusy("")
  }

  async function handleRevokeSessions() {
    if (!drawerUser) return
    setActionBusy("revoke_sessions")
    const res = await opsAdminApi.revokeOpsUserSessions(drawerUser.id)
    if (res.ok) {
      toast(`${res.revoked_count || 0} session(s) revoked.`, "success")
      setDrawerSessions((prev) => prev.map((s) => ({ ...s, is_active: false, is_revoked: true })))
      setUsers((prev) => prev.map((u) => u.id === drawerUser.id ? { ...u, active_session_count: 0 } : u))
    } else {
      showApiError(res as unknown as Record<string, unknown>, "Failed to revoke sessions.")
    }
    setActionBusy("")
  }

  async function copyText(value: string) {
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      toast("Copied.", "success")
    } catch {
      toast("Copy failed.", "error")
    }
  }

  useEffect(() => {
    loadUsers().catch(() => {})
  }, [activeFilter, roleFilter])

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault()
    loadUsers().catch(() => {})
  }

  function formatDate(iso: string) {
    if (!iso) return "—"
    const d = new Date(iso)
    if (isNaN(d.getTime())) return "—"
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
  }

  function formatDateTime(iso: string) {
    if (!iso) return "—"
    const d = new Date(iso)
    if (isNaN(d.getTime())) return "—"
    return d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
  }

  function timeAgo(iso: string) {
    if (!iso) return "never"
    const d = new Date(iso)
    if (isNaN(d.getTime())) return "never"
    const diffMs = Date.now() - d.getTime()
    if (diffMs < 60_000) return "just now"
    if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`
    if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`
    return `${Math.floor(diffMs / 86_400_000)}d ago`
  }

  return (
    <div className="ops-admin-page">
      <div className="page-header ops-page-header">
        <div className="ops-page-header__row">
          <h1 className="page-header__title" style={{ marginBottom: 0 }}>User Management</h1>
          <span className="ops-badge">Ops</span>
        </div>
        <p className="page-header__subtitle">
          Search, inspect, and manage user accounts, roles, and sessions.
        </p>
      </div>

      {/* Toolbar: Search + Filters */}
      <section className="ops-ai-toolbar">
        <form onSubmit={handleSearchSubmit} className="ops-ai-toolbar__field" style={{ flex: 2 }}>
          <span className="ops-ai-toolbar__label">Search</span>
          <div style={{ display: "flex", gap: "var(--sf-space-2)" }}>
            <input
              className="sf-field-select"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Email, name, or easy ID"
              style={{ flex: 1 }}
            />
            <button type="submit" className="data-table__action">Search</button>
          </div>
        </form>
        <label className="ops-ai-toolbar__field">
          <span className="ops-ai-toolbar__label">Status</span>
          <select
            className="sf-field-select"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as ActiveFilter)}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
        <label className="ops-ai-toolbar__field">
          <span className="ops-ai-toolbar__label">Role</span>
          <select
            className="sf-field-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
          >
            <option value="">All</option>
            <option value="owner">Owner</option>
            <option value="admin">Admin / Ops</option>
            <option value="platform_admin">Platform Admin</option>
          </select>
        </label>
      </section>

      {/* Summary */}
      <section className="ops-summary-grid">
        <div className="ops-summary-card">
          <div className="ops-summary-card__title">Users shown</div>
          <div className="ops-summary-card__line">{users.length}{hasMore ? "+" : ""}</div>
        </div>
        <div className="ops-summary-card">
          <div className="ops-summary-card__title">Active</div>
          <div className="ops-summary-card__line">{users.filter((u) => u.is_active).length}</div>
        </div>
        <div className="ops-summary-card">
          <div className="ops-summary-card__title">Admins</div>
          <div className="ops-summary-card__line">{users.filter((u) => u.role === "admin" || u.role === "ops").length}</div>
        </div>
        <div className="ops-summary-card">
          <div className="ops-summary-card__title">Platform Admins</div>
          <div className="ops-summary-card__line">{users.filter((u) => u.is_platform_admin).length}</div>
        </div>
      </section>

      {/* Users Table */}
      <section className="ops-card" style={{ marginTop: "var(--sf-space-4)" }}>
        {loading ? (
          <div className="ops-llm-empty">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="ops-llm-empty">No users found.</div>
        ) : (
          <>
            <div className="data-table__scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Properties</th>
                    <th>Sessions</th>
                    <th>Status</th>
                    <th>Last login</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      className={`data-table__row${selectedUserId === user.id ? " data-table__row--selected" : ""}${!user.is_active ? " data-table__row--muted" : ""}`}
                      onClick={() => openUserDrawer(user.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                          <span style={{ fontWeight: 500 }}>{user.email}</span>
                          {user.name && (
                            <span style={{ fontSize: "12px", opacity: 0.65 }}>{user.name}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="ops-role-badge" data-role={user.role}>
                          {user.role}
                        </span>
                        {user.is_platform_admin && (
                          <span className="ops-role-badge" data-role="platform" style={{ marginLeft: 4 }}>
                            platform
                          </span>
                        )}
                      </td>
                      <td>{user.property_count}</td>
                      <td>{user.active_session_count}</td>
                      <td>
                        <span className={`status-pill status-pill--${user.is_active ? "healthy" : "error"}`}>
                          {user.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td title={user.last_login_at}>{timeAgo(user.last_login_at)}</td>
                      <td>{formatDate(user.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div style={{ padding: "var(--sf-space-3)", textAlign: "center" }}>
                <button
                  type="button"
                  className="data-table__action"
                  onClick={() => loadUsers(true).catch(() => {})}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* User Detail Drawer */}
      {selectedUserId && (
        <div className="drawer-overlay" onClick={closeDrawer} role="presentation" aria-hidden="true">
          <div className="drawer drawer--wide" onClick={(e) => e.stopPropagation()}>
            <div className="drawer__header">
              <h3 className="drawer__title">User Detail</h3>
              <button type="button" className="drawer__close" onClick={closeDrawer}>×</button>
            </div>

            {drawerLoading ? (
              <div className="ops-llm-empty">Loading...</div>
            ) : !drawerUser ? (
              <div className="ops-llm-empty">User not found.</div>
            ) : (
              <div className="drawer__body">
                {/* Identity */}
                <div className="ops-drawer-section">
                  <div className="ops-drawer-section__title">Identity</div>
                  <div className="ops-drawer-kv">
                    <div className="ops-drawer-kv__row">
                      <span className="ops-drawer-kv__label">Email</span>
                      <span className="ops-drawer-kv__value">
                        {drawerUser.email}
                        <button type="button" className="ops-drawer-kv__copy" onClick={() => copyText(drawerUser.email)}>copy</button>
                      </span>
                    </div>
                    <div className="ops-drawer-kv__row">
                      <span className="ops-drawer-kv__label">Name</span>
                      <span className="ops-drawer-kv__value">{drawerUser.name || "—"}</span>
                    </div>
                    <div className="ops-drawer-kv__row">
                      <span className="ops-drawer-kv__label">Easy ID</span>
                      <span className="ops-drawer-kv__value">
                        {drawerUser.easy_id || "—"}
                        {drawerUser.easy_id && (
                          <button type="button" className="ops-drawer-kv__copy" onClick={() => copyText(drawerUser.easy_id)}>copy</button>
                        )}
                      </span>
                    </div>
                    <div className="ops-drawer-kv__row">
                      <span className="ops-drawer-kv__label">User ID</span>
                      <span className="ops-drawer-kv__value" style={{ fontSize: "11px", fontFamily: "monospace" }}>
                        {drawerUser.id}
                        <button type="button" className="ops-drawer-kv__copy" onClick={() => copyText(drawerUser.id)}>copy</button>
                      </span>
                    </div>
                    <div className="ops-drawer-kv__row">
                      <span className="ops-drawer-kv__label">Created</span>
                      <span className="ops-drawer-kv__value">{formatDateTime(drawerUser.created_at)}</span>
                    </div>
                    <div className="ops-drawer-kv__row">
                      <span className="ops-drawer-kv__label">Last login</span>
                      <span className="ops-drawer-kv__value">{drawerUser.last_login_at ? formatDateTime(drawerUser.last_login_at) : "never"}</span>
                    </div>
                    <div className="ops-drawer-kv__row">
                      <span className="ops-drawer-kv__label">Plan</span>
                      <span className="ops-drawer-kv__value">
                        <span className={`status-pill status-pill--${drawerUser.plan === "free" ? "neutral" : "healthy"}`} style={{ fontSize: "11px" }}>
                          {drawerUser.plan}
                        </span>
                        {drawerUser.is_trialing && (
                          <span className="status-pill status-pill--warning" style={{ fontSize: "11px", marginLeft: 4 }}>trial</span>
                        )}
                        {drawerUser.plan_status !== "none" && drawerUser.plan_status !== "active" && (
                          <span style={{ fontSize: "11px", opacity: 0.6, marginLeft: 4 }}>({drawerUser.plan_status})</span>
                        )}
                      </span>
                    </div>
                    {drawerUser.is_trialing && drawerUser.trial_ends_at && (
                      <div className="ops-drawer-kv__row">
                        <span className="ops-drawer-kv__label">Trial ends</span>
                        <span className="ops-drawer-kv__value">{formatDate(drawerUser.trial_ends_at)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Role & Status Controls */}
                <div className="ops-drawer-section">
                  <div className="ops-drawer-section__title">Role & Status</div>
                  <div className="ops-drawer-controls">
                    <div className="ops-drawer-controls__row">
                      <span>Status</span>
                      <span className={`status-pill status-pill--${drawerUser.is_active ? "healthy" : "error"}`}>
                        {drawerUser.is_active ? "Active" : "Inactive"}
                      </span>
                      <button
                        type="button"
                        className="data-table__action"
                        onClick={handleToggleActive}
                        disabled={actionBusy === "toggle_active"}
                      >
                        {drawerUser.is_active ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                    <div className="ops-drawer-controls__row">
                      <span>Role</span>
                      <select
                        className="sf-field-select"
                        value={drawerUser.role}
                        onChange={(e) => handleToggleRole(e.target.value)}
                        disabled={actionBusy === "toggle_role"}
                        style={{ width: "auto", minWidth: "100px" }}
                      >
                        <option value="owner">owner</option>
                        <option value="admin">admin</option>
                        <option value="ops">ops</option>
                      </select>
                    </div>
                    <div className="ops-drawer-controls__row">
                      <span>Platform Admin</span>
                      <span className={`status-pill status-pill--${drawerUser.is_platform_admin ? "warning" : "neutral"}`}>
                        {drawerUser.is_platform_admin ? "Yes" : "No"}
                      </span>
                      <button
                        type="button"
                        className="data-table__action"
                        onClick={handleTogglePlatformAdmin}
                        disabled={actionBusy === "toggle_platform_admin"}
                      >
                        {drawerUser.is_platform_admin ? "Revoke" : "Grant"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Properties */}
                <div className="ops-drawer-section">
                  <div className="ops-drawer-section__title">Properties ({drawerProperties.length})</div>
                  {drawerProperties.length === 0 ? (
                    <div className="ops-llm-empty" style={{ padding: "var(--sf-space-2)" }}>No properties.</div>
                  ) : (
                    <div className="ops-drawer-list">
                      {drawerProperties.map((p) => (
                        <div key={p.id} className="ops-drawer-list__item">
                          <div>
                            <strong>{p.name}</strong>
                            <span style={{ marginLeft: 6, fontSize: "11px", opacity: 0.6 }}>{p.slug}</span>
                          </div>
                          <div style={{ display: "flex", gap: "var(--sf-space-2)", fontSize: "12px", opacity: 0.7, marginTop: 2 }}>
                            <span>Role: {p.membership_role}</span>
                            <span>Tier: {p.tier}</span>
                            <span className={`status-pill status-pill--${p.is_active ? "healthy" : "error"}`} style={{ fontSize: "11px" }}>
                              {p.is_active ? "active" : "inactive"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sessions */}
                <div className="ops-drawer-section">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div className="ops-drawer-section__title" style={{ margin: 0 }}>
                      Sessions ({drawerSessions.filter((s) => s.is_active).length} active)
                    </div>
                    {drawerSessions.some((s) => s.is_active) && (
                      <button
                        type="button"
                        className="data-table__action"
                        onClick={handleRevokeSessions}
                        disabled={actionBusy === "revoke_sessions"}
                        style={{ color: "var(--sf-destructive, #dc2626)" }}
                      >
                        Revoke all
                      </button>
                    )}
                  </div>
                  {drawerSessions.length === 0 ? (
                    <div className="ops-llm-empty" style={{ padding: "var(--sf-space-2)" }}>No sessions.</div>
                  ) : (
                    <div className="ops-drawer-list">
                      {drawerSessions.map((s) => (
                        <div key={s.id} className="ops-drawer-list__item">
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ fontSize: "12px" }}>{s.ip_address || "unknown IP"}</span>
                            <span className={`status-pill status-pill--${s.is_active ? "healthy" : s.is_revoked ? "error" : "neutral"}`} style={{ fontSize: "11px" }}>
                              {s.is_active ? "active" : s.is_revoked ? "revoked" : "expired"}
                            </span>
                          </div>
                          <div style={{ fontSize: "11px", opacity: 0.6, marginTop: 2 }}>
                            {s.user_agent || "—"}
                          </div>
                          <div style={{ fontSize: "11px", opacity: 0.5, marginTop: 2 }}>
                            Created {formatDateTime(s.created_at)} · Expires {formatDateTime(s.expires_at)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent Audit */}
                <div className="ops-drawer-section">
                  <div className="ops-drawer-section__title">Recent Activity ({drawerAudit.length})</div>
                  {drawerAudit.length === 0 ? (
                    <div className="ops-llm-empty" style={{ padding: "var(--sf-space-2)" }}>No audit entries.</div>
                  ) : (
                    <div className="ops-drawer-list">
                      {drawerAudit.map((a, i) => (
                        <div key={i} className="ops-drawer-list__item">
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ fontWeight: 500, fontSize: "12px" }}>{a.action}</span>
                            <span style={{ fontSize: "11px", opacity: 0.6 }}>{timeAgo(a.created_at)}</span>
                          </div>
                          {a.property_id && (
                            <div style={{ fontSize: "11px", opacity: 0.5, marginTop: 2 }}>
                              Property: {a.property_id}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
